import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { isTerminalJobState, type JobDTO } from '@devrobot/shared';
import {
  Archive,
  ArchiveRestore,
  ArrowRightLeft,
  Ban,
  CircleArrowUp,
  CircleCheck,
  CircleHelp,
  CircleX,
  DatabaseZap,
  ExternalLink,
  KeyRound,
  KeySquare,
  ListChecks,
  Power,
  Rocket,
  Route,
  ServerCog,
  ServerOff,
  Share,
  SlidersHorizontal,
  Trash2,
  Undo2,
} from 'lucide-react';
import { ApiError, api, seg } from '../../api/client';
import { qk, useApiMutation, useJob } from '../../api/queries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyButton } from '../../components/CopyButton';
import { ErrorAlert } from '../../components/ErrorAlert';
import { JobStages } from '../../components/JobStages';
import { SourceBadge } from '../../components/JobsTable';
import { KeyValue } from '../../components/KeyValue';
import { LogViewer } from '../../components/LogViewer';
import { IconChip, PageHeader, Section } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { PageSkeleton } from '../../components/Skeleton';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toaster';
import { useNow } from '../../hooks/useNow';
import { haptic, share } from '../../kit/native';
import { durationBetween, formatDuration, shortDigest, shortSha } from '../../lib/format';
import NotFoundPage from '../NotFoundPage';
import { EnrollApproval } from './EnrollApproval';
import { useJobStream } from './useJobStream';

type Tone = 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error' | 'neutral';
type IconType = typeof Rocket;

/** Icon chip per job kind, so a job reads at a glance. */
const KIND_ICON: Record<string, { icon: IconType; tone: Tone }> = {
  deploy: { icon: Rocket, tone: 'primary' },
  lifecycle: { icon: Power, tone: 'info' },
  'apply-config': { icon: SlidersHorizontal, tone: 'secondary' },
  rollback: { icon: Undo2, tone: 'warning' },
  reroute: { icon: Route, tone: 'info' },
  remove: { icon: Trash2, tone: 'error' },
  'delete-data': { icon: DatabaseZap, tone: 'error' },
  backup: { icon: Archive, tone: 'success' },
  restore: { icon: ArchiveRestore, tone: 'warning' },
  enroll: { icon: ServerCog, tone: 'secondary' },
  'agent-upgrade': { icon: CircleArrowUp, tone: 'info' },
  'vps-remove': { icon: ServerOff, tone: 'error' },
  'ssh-ca-install': { icon: KeyRound, tone: 'secondary' },
  'ssh-ca-remove': { icon: KeySquare, tone: 'warning' },
  migrate: { icon: ArrowRightLeft, tone: 'primary' },
  'migrate-transfer': { icon: ArrowRightLeft, tone: 'info' },
  'migrate-cutover': { icon: Route, tone: 'warning' },
};

const RESULT_ALERT = {
  succeeded: { cls: 'alert alert-success alert-soft', Icon: CircleCheck },
  failed: { cls: 'alert alert-error alert-soft', Icon: CircleX },
  canceled: { cls: 'alert alert-warning alert-soft', Icon: Ban },
  unknown: { cls: 'alert alert-warning alert-soft', Icon: CircleHelp },
} as const;

function ResultPanel({ job }: { job: JobDTO }) {
  const r = job.result;
  const terminal = isTerminalJobState(job.state);
  if (!terminal && job.state !== 'unknown') return null;
  const key = (job.state in RESULT_ALERT ? job.state : 'unknown') as keyof typeof RESULT_ALERT;
  const { cls, Icon } = RESULT_ALERT[key];
  const heading =
    job.state === 'succeeded' ? 'Succeeded' : job.state === 'failed' ? `Failed${r.failureStage ? ` during “${r.failureStage}”` : ''}` : job.state === 'canceled' ? 'Canceled' : 'Outcome not confirmed';
  const changed =
    r.mutated === true ? 'Changes were made on the target.' : r.mutated === false ? 'No changes were made.' : job.state === 'unknown' ? 'It is not yet known whether changes were made.' : null;

  return (
    <div role={job.state === 'failed' ? 'alert' : 'status'} className={`${cls} animate-rise items-start rounded-box`}>
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-base font-semibold">{heading}</p>
        {r.message ? <p className="break-anywhere">{r.message}</p> : null}
        <div className="flex flex-wrap gap-2 text-sm">
          {changed ? <span className={`badge badge-sm ${r.mutated ? 'badge-soft badge-warning' : 'badge-ghost'}`}>{changed}</span> : null}
          {r.targetState ? <span className="badge badge-outline badge-sm">target state: {r.targetState}</span> : null}
        </div>
        {r.pipelineUrl ? (
          <p className="text-sm">
            <a className="link inline-flex items-center gap-1" href={r.pipelineUrl} target="_blank" rel="noreferrer">
              GitLab pipeline <ExternalLink className="size-3" aria-hidden="true" />
            </a>
          </p>
        ) : null}
        {r.observations && r.observations.length > 0 ? (
          <div>
            <p className="text-sm font-medium">Observations</p>
            <ul className="list-disc pl-5 text-sm">
              {r.observations.map((o, i) => (
                <li key={i} className="break-anywhere">
                  {o}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {r.nextSteps && r.nextSteps.length > 0 ? (
          <div>
            <p className="text-sm font-medium">What to do next</p>
            <ul className="list-disc pl-5 text-sm">
              {r.nextSteps.map((o, i) => (
                <li key={i} className="break-anywhere">
                  {o}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A small stat cell for the job's key facts. */
function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-field bg-base-200/60 p-3">
      <dt className="text-xs text-base-content/55">{label}</dt>
      <dd className="mt-1 truncate text-sm font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

/** Pulsing dot shown next to the state while the job is live. */
function LiveDot() {
  return (
    <span className="inline-grid *:[grid-area:1/1]" aria-hidden="true">
      <span className="status status-info animate-ping" />
      <span className="status status-info" />
    </span>
  );
}

const canShare = typeof navigator !== 'undefined' && 'share' in navigator;

export default function JobPage() {
  const { id = '' } = useParams();
  const q = useJob(id);
  const { logs, status } = useJobStream(id);
  const now = useNow();
  const toast = useToast();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const cancel = useApiMutation(() => api.post<JobDTO>(`/api/v1/jobs/${seg(id)}/cancel`), { invalidate: [qk.job(id), ['jobs']] });

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
    return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  }
  const job = q.data;
  const terminal = isTerminalJobState(job.state);
  const live = job.state === 'running';
  const isEnrollApproval = job.kind === 'enroll' && job.state === 'awaiting_approval';
  const kindIcon = KIND_ICON[job.kind] ?? { icon: ListChecks, tone: 'neutral' as const };
  const stagesDone = job.stages.filter((s) => s.state === 'succeeded' || s.state === 'skipped').length;
  const url = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Jobs', to: '/jobs' }, { label: <span className="font-mono">{job.id}</span> }]}
        icon={<IconChip icon={kindIcon.icon} tone={kindIcon.tone} size="lg" />}
        title={job.summary}
        badges={
          <>
            <span className="inline-flex items-center gap-1.5">
              {live ? <LiveDot /> : null}
              <StatusBadge kind="job" value={job.state} size="md" />
            </span>
            {job.cancelRequested && !terminal ? <span className="badge badge-soft badge-warning">cancellation requested</span> : null}
          </>
        }
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <span className="badge badge-ghost badge-sm font-mono">{job.kind}</span>
            {job.stage && !terminal ? <span>stage: {job.stage}</span> : null}
            <span>
              started by {job.actor.name || job.actor.username} via <SourceBadge source={job.source} />
            </span>
            <RelativeTime value={job.createdAt} />
          </span>
        }
        actions={
          <>
            {canShare ? (
              <button
                type="button"
                className="btn btn-square btn-ghost btn-sm"
                aria-label="Share link to this job"
                title="Share link to this job"
                onClick={() => void share({ title: `${job.summary} · DevRobot`, url })}
              >
                <Share className="size-4" aria-hidden="true" />
              </button>
            ) : (
              <CopyButton value={url} size="sm" label="Copy link to this job" />
            )}
            {!terminal && !job.cancelRequested ? (
              <button type="button" className="btn btn-ghost btn-sm dr-ink-warning" onClick={() => setConfirmCancel(true)}>
                <Ban className="size-4" aria-hidden="true" />
                Cancel job
              </button>
            ) : null}
          </>
        }
      />

      <dl className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Duration">{formatDuration(durationBetween(job.startedAt ?? job.createdAt, job.finishedAt, now))}</Stat>
        <Stat label="Stages">{job.stages.length > 0 ? `${stagesDone} / ${job.stages.length} done` : '—'}</Stat>
        <Stat label="Started by">
          <span className="inline-flex max-w-full items-center gap-1.5">
            <span className="truncate" title={`${job.actor.name} (@${job.actor.username})`}>
              {job.actor.name} (@{job.actor.username})
            </span>
            <SourceBadge source={job.source} />
          </span>
        </Stat>
        <Stat label="Created">
          <RelativeTime value={job.createdAt} />
        </Stat>
      </dl>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <ResultPanel job={job} />
          {isEnrollApproval ? <EnrollApproval job={job} /> : null}
          <Section title="Stages">
            <JobStages stages={job.stages} />
          </Section>
          <Section
            title="Logs"
            description={
              <span className="inline-flex items-center gap-2">
                {status === 'streaming' && !terminal ? <LiveDot /> : null}
                {status === 'polling' ? 'The live stream is unavailable; refreshing every 3 seconds.' : status === 'streaming' ? 'Streaming live.' : status === 'ended' ? 'The job has finished; this is the complete log.' : 'Connecting to the live stream…'}
              </span>
            }
          >
            <LogViewer
              busy={status === 'streaming' && !terminal}
              lines={logs.map((l) => ({ key: l.seq, ts: l.ts, level: l.level, source: l.stage, message: l.message }))}
              emptyText={terminal ? 'This job produced no log output.' : 'Waiting for output…'}
            />
          </Section>
        </div>
        <div className="min-w-0 space-y-6">
          <Section title="Summary">
            <KeyValue
              items={[
                { label: 'Kind', value: <span className="font-mono">{job.kind}</span> },
                { label: 'State', value: <StatusBadge kind="job" value={job.state} /> },
                {
                  label: 'Project',
                  value: job.project ? <Link className="link" to={`/projects/${job.project.key}`}>{job.project.name}</Link> : null,
                  hidden: !job.project,
                },
                { label: 'Environment', value: job.environment, hidden: !job.environment },
                { label: 'Target', value: job.targetId ? <Link className="link" to={`/targets/${job.targetId}`}>Open target</Link> : null, hidden: !job.targetId },
                { label: 'VPS', value: job.vps ? <Link className="link" to={`/vps/${job.vps.id}`}>{job.vps.name}</Link> : null, hidden: !job.vps },
                {
                  label: 'Branch / commit',
                  value: job.branch ? (
                    <span className="font-mono">
                      {job.branch}
                      {job.commitSha ? `@${shortSha(job.commitSha)}` : ''}
                    </span>
                  ) : null,
                  hidden: !job.branch && !job.commitSha,
                },
                { label: 'Actor', value: `${job.actor.name} (@${job.actor.username})` },
                { label: 'Source', value: <SourceBadge source={job.source} /> },
                { label: 'Created', value: <RelativeTime value={job.createdAt} /> },
                { label: 'Started', value: <RelativeTime value={job.startedAt} fallback="not started" /> },
                { label: 'Finished', value: <RelativeTime value={job.finishedAt} fallback="not finished" /> },
                { label: 'Duration', value: formatDuration(durationBetween(job.startedAt ?? job.createdAt, job.finishedAt, now)) },
              ]}
            />
            {job.release ? (
              <div className="space-y-1 rounded-field bg-base-200/60 p-3 text-sm">
                <p className="text-xs text-base-content/55">Release</p>
                <p className="font-mono text-xs font-medium">
                  {job.release.branch}@{job.release.shortSha}
                  {job.release.commitTitle ? ` — ${job.release.commitTitle}` : ''}
                </p>
                {job.release.pipelineUrl ? (
                  <a className="link inline-flex items-center gap-1 text-xs" href={job.release.pipelineUrl} target="_blank" rel="noreferrer">
                    Pipeline #{job.release.pipelineId} <ExternalLink className="size-3" aria-hidden="true" />
                  </a>
                ) : null}
                <ul className="text-xs text-base-content/65">
                  {Object.entries(job.release.imageDigests).map(([svc, d]) => (
                    <li key={svc} className="break-anywhere font-mono" title={d}>
                      {svc}: {shortDigest(d)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {Object.keys(job.params).length > 0 ? (
              <details className="collapse-arrow collapse rounded-field border border-hairline">
                <summary className="collapse-title min-h-0 py-2 text-sm font-medium">Parameters</summary>
                <div className="collapse-content">
                  <pre className="overflow-x-auto rounded-field bg-base-200/60 p-3 font-mono text-xs break-anywhere whitespace-pre-wrap">{JSON.stringify(job.params, null, 2)}</pre>
                </div>
              </details>
            ) : null}
          </Section>
        </div>
      </div>

      {confirmCancel ? (
        <ConfirmDialog
          title="Cancel this job?"
          confirmLabel="Request cancellation"
          tone="warning"
          busy={cancel.isPending}
          error={cancel.error}
          onCancel={() => {
            setConfirmCancel(false);
            cancel.reset();
          }}
          onConfirm={() =>
            cancel.mutate(undefined, {
              onSuccess: () => {
                setConfirmCancel(false);
                toast.info('Cancellation requested', { description: 'The job stops at the next safe point.' });
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p>DevRobot stops the job at the next safe point.</p>
          <div role="note" className="alert alert-warning alert-soft text-sm">
            Cancellation does not roll back. If the job already changed the target, those changes stay in place, and the result reports whether anything was changed.
          </div>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
